import React, { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CookieConsent from '../components/CookieConsent';

const Layout = () => {
  const [theme, setTheme] = useState('light');
  const [soundEnabled, setSoundEnabled] = useState(false);

  const morningAudioRef = useRef(null);
  const nightAudioRef = useRef(null);
  const fadeIntervalRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    if (savedTheme === 'dark') document.documentElement.classList.add('dark');

    const savedSound = localStorage.getItem('soundEnabled');
    if (savedSound === 'true') setSoundEnabled(true);
  }, []);

  useEffect(() => {
    const current = theme === 'light' ? morningAudioRef.current : nightAudioRef.current;
    const other = theme === 'light' ? nightAudioRef.current : morningAudioRef.current;
    if (!current || !other) return;
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);

    const fade = (audio, target, dur, cb) => {
      if (!audio) return cb && cb();
      const steps = 40;
      const step = (target - audio.volume) / steps;
      let s = 0;
      const id = setInterval(() => {
        s++;
        audio.volume = Math.max(0, Math.min(1, audio.volume + step));
        if (s >= steps) {
          clearInterval(id);
          audio.volume = target;
          cb && cb();
        }
      }, dur / steps);
      fadeIntervalRef.current = id;
    };

    if (soundEnabled) {
      fade(other, 0, 800, () => {
        other.pause();
        other.currentTime = 0;
      });
      current.volume = 0;
      current.play()
        .then(() => fade(current, 0.35, 1500))
        .catch(() => {});
    } else {
      fade(current, 0, 600, () => current.pause());
      fade(other, 0, 600, () => other.pause());
    }
  }, [soundEnabled, theme]);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.classList.toggle('dark', next === 'dark');
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('soundEnabled', next.toString());
  };

  return (
    <div className="relative min-h-screen transition-colors duration-500">
      <audio ref={morningAudioRef} loop preload="metadata" className="hidden">
        <source src="https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/qyp4js04_morning_wave_sound.mp4" type="audio/mp4" />
      </audio>
      <audio ref={nightAudioRef} loop preload="metadata" className="hidden">
        <source src="https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/ur1gcj8z_night_wave_sound.mp4" type="audio/mp4" />
      </audio>

      <Header
        theme={theme}
        toggleTheme={toggleTheme}
        soundEnabled={soundEnabled}
        toggleSound={toggleSound}
      />

      <main>
        <Outlet context={{ theme, toggleTheme, soundEnabled, toggleSound }} />
      </main>

      <Footer theme={theme} />

      <CookieConsent />
    </div>
  );
};

export default Layout;
